import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowLeft, 
  Download, 
  Copy, 
  Check, 
  ShieldCheck, 
  AlertCircle, 
  QrCode,
  CheckCircle2,
  Truck,
  ShoppingBag
} from 'lucide-react';
import { toJpeg } from 'html-to-image';
import { GIFFARINE_OFFICIAL_LOGO } from '../assets/logo';
import { DISTRIBUTOR_INFO } from '../data/products';
import { getCachedQrPaymentConfig, fetchQrPaymentConfig, QRPaymentConfig, subscribeQrPaymentConfig } from '../lib/qrPaymentService';
import { generatePromptPayDataUrl } from '../lib/promptpayQr';
import { ThaiQrPaymentBadge } from './payment/ThaiQrPaymentBadge';

interface QRPaymentProcessingViewProps {
  slug: string; // e.g. "98939522-PNK260919-V1EW" or "PNK260919-V1EW"
  currentUser?: any;
  onNavigateHome: () => void;
  onOpenAuth?: () => void;
}

export const QRPaymentProcessingView: React.FC<QRPaymentProcessingViewProps> = ({
  slug,
  currentUser,
  onNavigateHome,
  onOpenAuth
}) => {
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<any>(null);
  const [error, setError] = useState<string>('');
  
  // QR Config State
  const [qrConfig, setQrConfig] = useState<QRPaymentConfig>(() => getCachedQrPaymentConfig());
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [generatingQr, setGeneratingQr] = useState(false);

  // Copy Feedback
  const [copiedMerchantCode, setCopiedMerchantCode] = useState(false);
  const [copiedBillerId, setCopiedBillerId] = useState(false);

  // Slip capture ref
  const slipRef = useRef<HTMLDivElement>(null);
  const [downloadingSlip, setDownloadingSlip] = useState(false);

  const slugParts = slug.split('-');
  let parsedMemberId = '';
  let parsedOrderId = slug;

  // Pattern detection for Order ID in slug: 
  // Supports GFY..., PNK..., ORD..., GF... or the new numeric format YYMMDD...
  const isOrderId = (part: string) => {
    if (!part) return false;
    return part.startsWith('GFY') || 
           part.startsWith('PNK') || 
           part.startsWith('ORD') || 
           part.startsWith('GF') ||
           /^\d{6}/.test(part); // New format: YYMMDD...
  };

  if (slugParts.length >= 3 && isOrderId(slugParts[1])) {
    parsedMemberId = slugParts[0];
    parsedOrderId = `${slugParts[1]}-${slugParts[2]}`;
  } else if (slugParts.length === 2 && isOrderId(slugParts[1])) {
    parsedMemberId = slugParts[0];
    parsedOrderId = slugParts[1];
  }

  // Subscribe to live QR configuration changes & initial fetch
  useEffect(() => {
    fetchQrPaymentConfig().then((cfg) => {
      if (cfg) setQrConfig(cfg);
    });
    const unsub = subscribeQrPaymentConfig((cfg) => {
      setQrConfig(cfg);
    });
    return () => unsub();
  }, []);

  // Fetch draft / submitted order details
  const fetchOrder = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/orders/draft/${encodeURIComponent(slug)}`);
      const data = await res.json();

      if (res.ok && data.success && data.order) {
        setOrder(data.order);
      } else {
        // Fallback search in all orders
        const allRes = await fetch('/api/orders');
        if (allRes.ok) {
          const allData = await allRes.json();
          const found = Array.isArray(allData)
            ? allData.find((o: any) => o.orderId.toLowerCase() === parsedOrderId.toLowerCase() || o.orderId.toLowerCase() === slug.toLowerCase())
            : null;
          if (found) {
            setOrder(found);
          } else {
            setError(data.error || 'ไม่พบข้อมูลคำสั่งซื้อในระบบ');
          }
        } else {
          setError(data.error || 'ไม่พบข้อมูลคำสั่งซื้อในระบบ');
        }
      }
    } catch (err: any) {
      console.error('Fetch order error in QR view:', err);
      setError('เกิดข้อผิดพลาดในการโหลดข้อมูลคำสั่งซื้อ กรุณาลองใหม่อีกครั้ง');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [slug]);

  // Generate dynamic PromptPay EMVCo QR Code
  useEffect(() => {
    if (!order) return;

    const generateQr = async () => {
      setGeneratingQr(true);
      try {
        const activePromptPayId = qrConfig.promptPayId || '0939678649';
        const activeBillerId = qrConfig.billerId || '010753700088205';
        // Enforce Merchant Code strictly to QV353070BI06881536X
        const activeMerchantCode = 'QV353070BI06881536X';
        const activeMerchantName = qrConfig.name || 'GIFFFORYOU.COM';
        const activeAmount = order.totalAmount || 0;
        // Ref 1: รหัสผู้ค้า (Merchant Code: QV353070BI06881536X ต้องเป็นค่านี้เท่านั้น)
        const ref1 = activeMerchantCode;
        // Ref 2: รหัสคำสั่งซื้อ
        const ref2 = order.orderId || parsedOrderId;

        const dataUrl = await generatePromptPayDataUrl({
          promptPayId: activePromptPayId,
          billerId: activeBillerId,
          merchantCode: activeMerchantCode,
          merchantName: activeMerchantName,
          amount: activeAmount,
          ref1,
          ref2,
          isDynamic: true
        });

        setQrDataUrl(dataUrl);
      } catch (err) {
        console.error('Error generating PromptPay QR Code:', err);
      } finally {
        setGeneratingQr(false);
      }
    };

    generateQr();
  }, [order, qrConfig]);

  // 15:00 countdown timer
  const handleCopy = (text: string, type: 'merchantCode' | 'billerId') => {
    navigator.clipboard.writeText(text);
    if (type === 'merchantCode') {
      setCopiedMerchantCode(true);
      setTimeout(() => setCopiedMerchantCode(false), 2000);
    } else if (type === 'billerId') {
      setCopiedBillerId(true);
      setTimeout(() => setCopiedBillerId(false), 2000);
    }
  };

  // Capture & Download full Sales Slip with QR Code at bottom (JPG HD)
  const handleDownloadSlipJpg = async () => {
    if (!slipRef.current || !order) return;
    try {
      setDownloadingSlip(true);
      const dataUrl = await toJpeg(slipRef.current, {
        cacheBust: true,
        quality: 0.96,
        pixelRatio: 2.5,
        backgroundColor: '#ffffff'
      });
      const link = document.createElement('a');
      link.download = `Slip-${order.orderId || parsedOrderId}.jpg`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Failed to download slip image:', err);
      alert('เกิดข้อผิดพลาดในการบันทึกภาพสลิป กรุณาลองใหม่อีกครั้ง');
    } finally {
      setDownloadingSlip(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900/5 flex flex-col items-center justify-center p-4">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-[#0E2C58] rounded-full animate-spin mb-4" />
        <p className="text-slate-700 text-sm font-semibold">กำลังเชื่อมต่อระบบชำระเงิน QR Payment...</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 text-center">
        <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center text-rose-500 mb-4 border border-rose-100">
          <AlertCircle className="w-8 h-8" />
        </div>
        <h2 className="text-lg font-bold text-slate-800 mb-2">ไม่พบรายการคำสั่งซื้อ</h2>
        <p className="text-slate-500 text-xs mb-6 max-w-md">{error || 'ไม่พบคำสั่งซื้อที่ตรงกับรหัสนี้ หรือคำสั่งซื้อถูกยกเลิกแล้ว'}</p>
        <button
          type="button"
          onClick={onNavigateHome}
          className="px-6 py-2.5 bg-gradient-to-r from-[#0E2C58] to-[#2A59FE] text-white rounded-xl text-xs font-bold shadow-md shadow-blue-900/20 hover:opacity-95 transition-all cursor-pointer"
        >
          กลับหน้าหลัก
        </button>
      </div>
    );
  }

  const orderItems = order.items || [];
  const totalAmount = order.totalAmount || 0;
  const subtotal = order.subtotal || totalAmount;
  const deliveryFee = order.deliveryFee !== undefined ? order.deliveryFee : (totalAmount >= 1000 ? 0 : 50);
  const totalPv = order.totalPv || 0;
  const isQrPayment = order.paymentMethod === 'BANK_TRANSFER' || order.paymentMethod === 'QR_PAYMENT' || order.paymentMethod === 'PROMPTPAY';

  const maskPhone = (phone: string): string => {
    if (!phone) return '-';
    const cleanPhone = phone.trim();
    if (cleanPhone.length <= 6) return cleanPhone;
    return cleanPhone.slice(0, 6) + '*'.repeat(Math.max(0, cleanPhone.length - 6));
  };

  const maskAddress = (address: string): string => {
    if (!address) return '-';
    const cleanAddress = address.trim();
    if (cleanAddress.length <= 6) return cleanAddress;
    return cleanAddress.slice(0, 6) + '*'.repeat(Math.min(25, Math.max(0, cleanAddress.length - 6)));
  };

  const formatThaiDateTimeCustom = (dateVal: any) => {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return '-';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} :${hours}:${minutes}`;
  };

  return (
    <div className="min-h-screen bg-slate-100 text-slate-800 py-6 sm:py-10 px-3 sm:px-6 font-['Prompt',sans-serif]">
      <div className="max-w-[410px] mx-auto">
        
        {/* Top Navigation Bar */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <button
            type="button"
            onClick={onNavigateHome}
            className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-slate-600 hover:text-slate-900 bg-white hover:bg-slate-50 px-4 py-2 rounded-xl border border-slate-200 transition-all cursor-pointer shadow-xs active:scale-95"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>กลับหน้าร้านค้า</span>
          </button>

          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-bold">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            <span>ระบบปลอดภัย 256-bit</span>
          </div>
        </div>

        {/* Centered Official Payment Card / Slip with QR Code */}
        <div 
          ref={slipRef}
          id="giffarine-checkout-slip"
          className="bg-white text-slate-900 rounded-3xl shadow-xl overflow-hidden border border-slate-200/80 p-5 sm:p-6 relative font-['Prompt',sans-serif] w-full"
        >
          {/* Elegant repeating 45-degree watermark overlay */}
          <div className="absolute inset-0 pointer-events-none select-none overflow-hidden z-0 flex flex-wrap justify-around items-center opacity-[0.035] p-4 gap-y-20 gap-x-10">
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="text-[#0E2C58] font-bold text-[10px] tracking-widest uppercase transform -rotate-45 p-2 border border-[#0E2C58]/25 rounded-md"
                style={{ minWidth: '110px', textAlign: 'center' }}
              >
                GIFFFORYOU.COM
              </div>
            ))}
          </div>

          {/* Slip Header & Branding */}
          <div className="text-center pb-4 border-b border-dashed border-slate-300 relative z-10">
            <div className="inline-flex items-center justify-center px-5 py-2 rounded-xl bg-[#0E2C58] mb-2 shadow-xs">
              <img
                src={GIFFARINE_OFFICIAL_LOGO}
                alt="Giffarine Logo"
                className="h-6 w-auto object-contain brightness-0 invert"
              />
            </div>

            <h1 className="text-[14px] font-extrabold text-slate-800 tracking-wide mt-1">
              GIFFARINE ONLINE STORE
            </h1>
            <p className="text-[10.5px] text-slate-500 font-medium">
              ร้านผู้จัดจำหน่ายกิฟฟารีนอิสระอย่างเป็นทางการ
            </p>

            {/* Status Badge */}
            <div className="mt-2.5 inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-[#E8F5E9] border border-[#C8E6C9] text-[#2E7D32] text-xs font-bold shadow-2xs whitespace-nowrap">
              <CheckCircle2 className="w-3.5 h-3.5 text-[#2E7D32] shrink-0" />
              <span>บันทึกคำสั่งซื้อสำเร็จ</span>
            </div>
          </div>

          {/* Order Meta Details */}
          <div className="py-3.5 border-b border-dashed border-slate-300 text-xs space-y-2 text-slate-700 relative z-10">
            <div className="flex justify-between items-center">
              <span className="text-slate-500 font-medium"># เลขที่คำสั่งซื้อ:</span>
              <span className="font-mono font-bold text-slate-800 bg-[#F1F5F9] px-2.5 py-0.5 rounded-lg text-[11px] border border-slate-200/50">
                {order.orderId || parsedOrderId}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-slate-500 font-medium"># รหัสสมาชิก (MID):</span>
              <span className="font-mono font-bold text-[#0E2C58] bg-slate-100 px-2.5 py-0.5 rounded-lg text-[11px] border border-slate-200">
                108-037654
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-slate-500 font-medium"># เลขที่ลูกค้า:</span>
              <span className="font-mono font-bold text-[#2A59FE] bg-blue-50/70 px-2.5 py-0.5 rounded-lg text-[11px] border border-blue-100">
                {parsedMemberId || order.customerId || '98939522'}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-slate-500 font-medium">วันที่-เวลา:</span>
              <span className="font-mono text-slate-800 font-semibold">
                {formatThaiDateTimeCustom(order.timestamp || new Date())}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-slate-500 font-medium">ช่องทางชำระเงิน:</span>
              <span className="font-bold text-slate-800 uppercase text-right">
                {isQrPayment ? 'คิวอาร์โค้ด / PROMPTPAY' : 'เก็บเงินปลายทาง (COD)'}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-slate-500 font-medium">สถานะชำระ:</span>
              <span className="px-3 py-0.5 rounded-full bg-[#E3F2FD] border border-[#BBDEFB] text-[#1565C0] text-[10.5px] font-bold">
                รอชำระ
              </span>
            </div>
          </div>

          {/* Order Items Table */}
          <div className="py-3.5 border-b border-dashed border-slate-300 text-xs relative z-10">
            <div className="flex justify-between text-[11px] font-semibold text-slate-400 mb-2.5 uppercase tracking-wider">
              <span>รายการสินค้า</span>
              <span>จำนวน / รวม</span>
            </div>

            <div className="space-y-3">
              {orderItems.map((item: any, idx: number) => (
                <div key={idx} className="flex justify-between items-start text-xs gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-800 leading-snug">{item.productName || item.name}</div>
                    <div className="text-slate-400 text-[10px] mt-0.5 font-mono">
                      รหัส: {item.productCode || '82044'}
                    </div>
                  </div>
                  <div className="font-mono text-slate-800 shrink-0 text-right">
                    <span className="text-slate-400 mr-2 text-[10.5px]">x{item.quantity}</span>
                    <strong className="font-bold text-slate-900 text-[12px]">฿{((item.pricePerUnit || item.price || 0) * (item.quantity || 1)).toLocaleString()}</strong>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Financial Totals */}
          <div className="py-3.5 text-xs space-y-2 text-slate-700 relative z-10">
            <div className="flex justify-between items-center text-slate-500 text-[11.5px]">
              <span>ยอดรวมสินค้า:</span>
              <span className="font-mono font-medium text-slate-800">฿{subtotal.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center text-slate-500 text-[11.5px]">
              <span>ค่าจัดส่ง:</span>
              <span className="font-mono font-medium text-slate-800">
                {deliveryFee === 0 ? 'จัดส่งฟรี' : `฿${deliveryFee.toLocaleString()}`}
              </span>
            </div>
            <div className="flex justify-between items-center text-slate-500 text-[11.5px]">
              <span>คะแนนสะสม (PV):</span>
              <span className="font-mono font-bold text-[#0E2C58]">{totalPv.toLocaleString()} PV</span>
            </div>
            
            {/* Thick black line directly from the image */}
            <div className="border-t-2 border-slate-900 my-1.5" />

            <div className="pt-1 flex justify-between items-baseline">
              <span className="text-sm font-extrabold text-slate-900">ยอดชำระสุทธิ:</span>
              <span className="text-2xl sm:text-3xl font-black text-[#0E2C58] font-mono tracking-tight">
                ฿{totalAmount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>

          {/* ข้อมูลผู้รับสินค้า (จัดแสดงตำแหน่งด้านล่างยอดชำระสุทธิตามข้อกำหนดสถาปัตยกรรม) */}
          <div className="py-3.5 border-t-2 border-dashed border-slate-300 text-xs text-slate-700 relative z-10">
            <div className="font-bold text-[#0E2C58] flex items-center gap-1.5 text-[12px] mb-2">
              <Truck className="w-4 h-4 text-[#2A59FE] shrink-0" />
              <span>ข้อมูลผู้รับสินค้า & จัดส่ง</span>
            </div>
            <div className="bg-slate-50/90 p-3 rounded-2xl border border-slate-200/90 space-y-2">
              <div className="flex justify-between items-center text-[12.5px] font-bold text-slate-800">
                <span className="flex items-center gap-1.5">
                  <span className="text-slate-500 text-[11px] font-normal">ชื่อผู้รับ:</span>
                  <span>{order.customerName || 'คุณลูกค้ากิฟฟารีน'}</span>
                </span>
                <span className="text-slate-700 font-mono text-[11.5px] bg-white px-2.5 py-0.5 rounded-lg border border-slate-200 font-semibold shadow-2xs">
                  โทร: {maskPhone(order.phone || '-')}
                </span>
              </div>
              <div className="text-slate-600 text-[11px] leading-relaxed pt-1.5 border-t border-slate-200/60">
                <span className="text-slate-400 mr-1.5 font-medium">ที่อยู่:</span>
                <span>{maskAddress(order.address || '-')}</span>
              </div>
            </div>
          </div>

          {/* ========================================================= */}
          {/* ด้านล่างสลิป: แสดงคิวอาโค้ด (QR CODE AT BOTTOM OF SLIP) */}
          {/* ========================================================= */}
          {isQrPayment && (
            <div className="mt-4 pt-4 border-t-2 border-dashed border-slate-300 text-center relative z-10">
              <div className="flex flex-col items-center justify-center gap-1.5 mb-2.5">
                <div className="h-10 w-[96px] sm:w-[104px] flex items-center justify-center rounded-xl overflow-hidden shadow-xs border border-[#00387A] bg-[#002D63]">
                  <ThaiQrPaymentBadge className="w-full h-full object-contain" />
                </div>
                <div className="inline-flex items-center justify-center gap-1.5 px-3 py-0.5 rounded-full bg-[#002D63] text-white text-[10px] font-bold shadow-2xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00A796]" />
                  <span>ระบบมาตรฐาน THAI QR PAYMENT • พร้อมเพย์</span>
                </div>
              </div>
              <h3 className="text-sm font-extrabold text-slate-900 leading-snug">สแกน QR Code ด้านล่างนี้เพื่อชำระเงิน</h3>
              <p className="text-[10.5px] text-slate-400 mt-0.5 mb-3 leading-relaxed">
                รองรับ Mobile Banking ทุกธนาคาร (กสิกร, ไทยพาณิชย์, กรุงเทพ, กรุงไทย, ออมสิน ฯลฯ)
              </p>

              {/* QR Code Presentation Box with Gray border & no center logo block as requested */}
              <div className="p-3.5 bg-white rounded-2xl border border-slate-200 shadow-xs inline-block mx-auto relative">
                {generatingQr ? (
                  <div className="w-48 h-48 flex flex-col items-center justify-center">
                    <div className="w-7 h-7 border-3 border-blue-100 border-t-[#2A59FE] rounded-full animate-spin mb-2" />
                    <span className="text-[10px] text-slate-400">กำลังสร้าง QR Code...</span>
                  </div>
                ) : qrDataUrl ? (
                  <div className="relative">
                    <img
                      src={qrDataUrl}
                      alt="PromptPay QR Code"
                      className="w-44 h-44 sm:w-48 sm:h-48 object-contain mx-auto"
                      crossOrigin="anonymous"
                    />
                  </div>
                ) : (
                  <div className="w-48 h-48 flex items-center justify-center text-xs text-slate-400">
                    ไม่สามารถสร้าง QR Code ได้
                  </div>
                )}
              </div>
              
              {/* Payment Reference Details */}
              <div className="mt-2.5 space-y-0.5 text-[10.5px] font-mono text-slate-700">
                <div><span className="text-slate-500 font-sans">รหัสสมาชิก (MID):</span> <strong className="font-bold text-[#0E2C58]">108-037654</strong></div>
                <div><span className="text-slate-500 font-sans">Biller ID:</span> <strong className="font-bold text-slate-800">{qrConfig.billerId || '010753700088205'}</strong></div>
                <div><span className="text-slate-500 font-sans">รหัสผู้ค้า (Ref 1):</span> <strong className="font-bold text-blue-900">QV353070BI06881536X</strong></div>
                <div><span className="text-slate-500 font-sans">รหัสคำสั่งซื้อ (Ref 2):</span> <strong className="font-bold text-slate-800">{order.orderId || parsedOrderId}</strong></div>
              </div>

              {/* Admin notice directly below QR */}
              <div className="mt-3.5 text-[10.5px] text-slate-400 leading-normal max-w-[320px] mx-auto">
                * แอดมินจะตรวจสอบยอดเงินและแนบสลิปยืนยันเข้าระบบหลังบ้านให้อัตโนมัติหลังสแกนชำระเงินสำเร็จ
              </div>
            </div>
          )}

          {/* Slip Document Footer */}
          <div className="mt-5 pt-3 border-t border-dashed border-slate-300 text-center text-[11px] text-slate-500 space-y-1 relative z-10">
            <p className="font-medium text-slate-700">ขอบพระคุณที่ไว้วางใจผลิตภัณฑ์กิฟฟารีน</p>
            <p className="text-[10.5px]">
              ติดต่อสอบถาม / ติดตามสถานะจัดส่ง LINE:{' '}
              <span className="font-bold text-[#0E2C58]">@{DISTRIBUTOR_INFO.lineId.replace('@', '')}</span>
            </p>
            <div className="pt-1.5 flex items-center justify-center gap-1 text-[9px] text-slate-400 font-mono">
              <ShieldCheck className="w-3 h-3 text-emerald-600" />
              <span>SECURITY VERIFIED TRANSACTION SLIP</span>
            </div>
          </div>
        </div>

        {/* Action Button: Save Slip with QR (JPG) */}
        <div className="w-full mt-4">
          <button
            type="button"
            onClick={handleDownloadSlipJpg}
            disabled={downloadingSlip}
            className="w-full py-3.5 px-4 rounded-2xl bg-gradient-to-r from-[#0E2C58] via-[#2A59FE] to-[#4F46E5] hover:opacity-95 text-white font-bold text-xs sm:text-sm flex items-center justify-center gap-2 shadow-lg shadow-blue-900/20 active:scale-95 transition-all cursor-pointer disabled:opacity-60"
          >
            <Download className="w-4 h-4" />
            <span>{downloadingSlip ? 'กำลังบันทึกสลิป...' : 'บันทึกภาพสลิปใบเสร็จ (JPG)'}</span>
          </button>
        </div>

      </div>
    </div>
  );
};
